// qs-brush-Spray.ts
import type { KonvaCircle, KonvaLine } from '@/types/qs-type-konva'
import Konva from 'konva'
import { BaseBrush } from '../base/qs-brushes-base'
import type { BrushConfig, BrushContext, BrushDrawingResult, BrushProperties, BrushRecreationContext, BrushDot } from '../qs-brushes-types'

export class SprayBrush extends BaseBrush {
  private lastSprayTime = 0
  private sprayThrottle = 50 // Minimum milliseconds between spray bursts
  private lastPosition: { x: number; y: number } | null = null

  readonly config: BrushConfig = {
    id: 'spray',
    name: 'Spray Brush',
    category: 'texture',
    description: 'Spray effect with random dots',
    icon: '✨',
    minSize: 5,
    maxSize: 50,
    defaultSize: 15
  }

  getProperties(size: number): BrushProperties {
    return {
      lineCap: 'round',
      lineJoin: 'round',
      dashPattern: [],
      strokeWidth: size,
      isSpray: true,
      originalStrokeWidth: size
    }
  }

  createBrushLine(context: BrushContext): KonvaLine {
    this.lastPosition = { x: context.position.x, y: context.position.y }

    return new Konva.Line({
      id: `path-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      points: [context.position.x, context.position.y],
      stroke: 'transparent',
      strokeWidth: 0,
      opacity: 0,
      visible: false,
      draggable: false,
      isSprayBrush: true,
      originalStrokeWidth: context.size
    })
  }

  continueDrawing(existingLine: KonvaLine, context: BrushContext): void {
    const currentPoints = existingLine.points()
    const newPoints = currentPoints.concat([context.position.x, context.position.y])
    existingLine.points(newPoints)

    if (this.lastPosition) {
      const distance = Math.sqrt(
        Math.pow(context.position.x - this.lastPosition.x, 2) +
        Math.pow(context.position.y - this.lastPosition.y, 2)
      )

      if (distance > 5) {
        this.createSprayDots(context)
        this.lastPosition = { x: context.position.x, y: context.position.y }
      }
    } else {
      this.createSprayDots(context)
      this.lastPosition = { x: context.position.x, y: context.position.y }
    }
  }

  async finishDrawing(existingLine: KonvaLine, context: BrushContext): Promise<BrushDrawingResult> {
    const points = existingLine.points()
    const properties = this.getProperties(context.size)
    const pathId = existingLine.id()

    const existingDots: KonvaCircle[] = []
    if (context.konvaLayer) {
      const sprayDots = context.konvaLayer.find('.spray-dot')
      sprayDots.forEach((dot: KonvaCircle) => {
        if (dot.attrs.className === 'temp-drawing') {
          dot.attrs.className = ''
          existingDots.push(dot)
        }
      })
    }

    const sprayDots = existingDots.length > 0 ? existingDots : this.createSprayDotsFromPath(points, context)

    const originalPath = this.createOriginalPath(points, context, pathId)

    return {
      konvaObjects: sprayDots,
      originalPath: originalPath,
      pathData: {
        id: pathId,
        layerId: context.layerId,
        points: points,
        stroke: context.color,
        strokeWidth: properties.strokeWidth,
        lineCap: properties.lineCap,
        lineJoin: properties.lineJoin,
        dash: properties.dashPattern || [],
        opacity: context.opacity,
        timestamp: Date.now(),
        brushType: this.config.id,
        isTapered: false,
        isThickToThin: false,
        isThinToThick: false,
        isSpray: true,
        isDiagonal: false,
        segmentCount: 0,
        dotCount: sprayDots.length,
        visualData: {
          segments: [],
          dots: sprayDots.map(dot => ({
            x: dot.x(),
            y: dot.y(),
            radius: dot.radius(),
            opacity: dot.opacity()
          }))
        }
      }
    }
  }

  async recreateFromData(context: BrushRecreationContext): Promise<void> {
    const { pathData, konvaLayer } = context

    if (pathData.visualData?.dots && pathData.visualData.dots.length > 0) {
      const sprayGroup = new Konva.Group({
        id: pathData.id,
        draggable: false
      })

      pathData.visualData.dots.forEach((dotData: BrushDot) => {
        const dot = new Konva.Circle({
          x: dotData.x,
          y: dotData.y,
          radius: dotData.radius,
          fill: pathData.stroke,
          opacity: dotData.opacity,
          draggable: false,
          name: 'spray-dot',
          sprayPathId: pathData.id
        })
        sprayGroup.add(dot)
      })

      konvaLayer.add(sprayGroup)

      const originalPath = this.createOriginalPath(Array.from(pathData.points), {
        position: { x: 0, y: 0 },
        size: pathData.strokeWidth,
        color: pathData.stroke,
        opacity: pathData.opacity,
        layerId: ''
      }, pathData.id)

      konvaLayer.add(originalPath)
    } else {
      const sprayGroup = new Konva.Group({
        id: pathData.id,
        draggable: false
      })

      const sprayDots = this.createSprayDotsFromPath(Array.from(pathData.points), {
        position: { x: 0, y: 0 },
        size: pathData.strokeWidth,
        color: pathData.stroke,
        opacity: pathData.opacity,
        layerId: ''
      })

      sprayDots.forEach(dot => sprayGroup.add(dot))
      konvaLayer.add(sprayGroup)

      const originalPath = this.createOriginalPath(Array.from(pathData.points), {
        position: { x: 0, y: 0 },
        size: pathData.strokeWidth,
        color: pathData.stroke,
        opacity: pathData.opacity,
        layerId: ''
      }, pathData.id)

      konvaLayer.add(originalPath)
    }
  }

  private createSprayDots(context: BrushContext): void {
    if (!context.konvaLayer) return
    if (!context.position || typeof context.position.x !== 'number' || typeof context.position.y !== 'number') return
    if (context.size <= 0 || !isFinite(context.size)) return

    const now = Date.now()
    if (now - this.lastSprayTime < this.sprayThrottle) return
    this.lastSprayTime = now

    const sprayRadius = context.size * 1.5
    const dotCount = Math.min(Math.max(3, Math.floor(context.size / 3)), 8)

    for (let i = 0; i < dotCount; i++) {
      const angle = Math.random() * 2 * Math.PI
      const distance = Math.random() * sprayRadius

      const x = context.position.x + Math.cos(angle) * distance
      const y = context.position.y + Math.sin(angle) * distance

      if (!isFinite(x) || !isFinite(y)) continue

      const dotSize = (context.size * 0.1) + Math.random() * (context.size * 0.3)

      if (!isFinite(dotSize) || dotSize <= 0) continue

      const dot = new Konva.Circle({
        x: x,
        y: y,
        radius: dotSize,
        fill: context.color,
        opacity: context.opacity,
        draggable: false,
        name: 'spray-dot'
      })

      dot.attrs.className = 'temp-drawing'
      context.konvaLayer.add(dot)
    }
  }

  private createSprayDotsFromPath(points: number[], context: BrushContext): KonvaCircle[] {
    const sprayRadius = context.size * 1.5
    const totalLength = this.calculateLineLength(points)
    const dotCount = Math.min(
      Math.max(10, Math.floor(totalLength / 10)),
      50
    )

    const dots: KonvaCircle[] = []

    for (let i = 0; i < dotCount; i++) {
      const segmentIndex = Math.floor(Math.random() * (points.length - 2) / 2) * 2
      const t = Math.random()

      if (segmentIndex < 0 || segmentIndex + 3 >= points.length) continue

      const x1 = points[segmentIndex]
      const y1 = points[segmentIndex + 1]
      const x2 = points[segmentIndex + 2]
      const y2 = points[segmentIndex + 3]

      if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue

      const lineX = x1 + t * (x2 - x1)
      const lineY = y1 + t * (y2 - y1)

      const angle = Math.random() * 2 * Math.PI
      const distance = Math.random() * sprayRadius

      const x = lineX + Math.cos(angle) * distance
      const y = lineY + Math.sin(angle) * distance

      const dotSize = (context.size * 0.1) + Math.random() * (context.size * 0.3)

      const dot = new Konva.Circle({
        x: x,
        y: y,
        radius: dotSize,
        fill: context.color,
        opacity: context.opacity,
        draggable: false,
        name: 'spray-dot'
      })

      dots.push(dot)
    }

    return dots
  }

  private calculateLineLength(points: number[]): number {
    let totalLength = 0
    for (let i = 0; i < points.length - 2; i += 2) {
      const x1 = points[i]
      const y1 = points[i + 1]
      const x2 = points[i + 2]
      const y2 = points[i + 3]

      if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue

      const dx = x2 - x1
      const dy = y2 - y1
      const segmentLength = Math.sqrt(dx * dx + dy * dy)
      if (isFinite(segmentLength)) {
        totalLength += segmentLength
      }
    }
    return totalLength
  }
}